import { z } from 'zod';
import { chatMessageSchema, type ChatMessage } from '@/entities/chat-message';
import type { HigherPeriod } from '@/entities/types';

/** DTO schema for serialized localStorage data (dates as strings) */
const chatMessageDtoSchema = chatMessageSchema.extend({
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

const legacyTextBlockSchema = z.object({
  type: z.literal('text'),
  content: z.string(),
});

const legacyBlocksMessageDtoSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  role: z.enum(['user', 'assistant']),
  blocks: z.array(
    z.discriminatedUnion('type', [
      legacyTextBlockSchema,
      z.object({
        type: z.literal('entity'),
        entityType: z.literal('task'),
        data: z.object({
          title: z.string(),
          parentGoalId: z.guid(),
        }),
      }),
    ]),
  ),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

const legacyMessageDtoSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

type ChatMessageDto = z.infer<typeof chatMessageDtoSchema>;
type LegacyBlocksMessageDto = z.infer<typeof legacyBlocksMessageDtoSchema>;
type LegacyMessageDto = z.infer<typeof legacyMessageDtoSchema>;

function migrateFromLegacyBlocks(dto: LegacyBlocksMessageDto): ChatMessageDto {
  return {
    id: dto.id,
    conversationId: dto.conversationId,
    role: dto.role,
    blocks: dto.blocks.map((block) => {
      if (block.type === 'text') {
        return { type: 'text' as const, data: block.content };
      }
      return { ...block, status: 'suggested' as const };
    }),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

function migrateFromLegacy(dto: LegacyMessageDto): ChatMessageDto {
  return {
    id: dto.id,
    conversationId: dto.conversationId,
    role: dto.role,
    blocks: [{ type: 'text', data: dto.content }],
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

function toChatMessage(dto: ChatMessageDto): ChatMessage {
  return {
    ...dto,
    createdAt: new Date(dto.createdAt),
    updatedAt: new Date(dto.updatedAt),
  };
}

function parseMessageItem(item: unknown): ChatMessageDto | null {
  const currentResult = chatMessageDtoSchema.safeParse(item);
  if (currentResult.success) {
    return currentResult.data;
  }

  const legacyBlocksResult = legacyBlocksMessageDtoSchema.safeParse(item);
  if (legacyBlocksResult.success) {
    return migrateFromLegacyBlocks(legacyBlocksResult.data);
  }

  const legacyResult = legacyMessageDtoSchema.safeParse(item);
  if (legacyResult.success) {
    return migrateFromLegacy(legacyResult.data);
  }

  return null;
}

function toChatMessages(data: unknown): ChatMessage[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map(parseMessageItem)
    .filter((dto): dto is ChatMessageDto => dto !== null)
    .map(toChatMessage);
}

function readMessages(conversationId: string): ChatMessage[] {
  try {
    const key = `chat-messages:${conversationId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored) as unknown;
    return toChatMessages(parsed);
  } catch {
    // If parsing fails, return empty array
    return [];
  }
}

function upsertMessages(conversationId: string, messages: ChatMessage[]): void {
  const key = `chat-messages:${conversationId}`;
  localStorage.setItem(key, JSON.stringify(messages));
}

function clearAllMessages(): void {
  Object.keys(localStorage)
    .filter((key) => key.startsWith('chat-messages:'))
    .forEach((key) => {
      localStorage.removeItem(key);
    });
}

function markPeriodSeen(period: HigherPeriod, startAtLocal: string): void {
  const key = `plan-seen:${period}:${startAtLocal}`;
  localStorage.setItem(key, '1');
}

function isPeriodSeen(period: HigherPeriod, startAtLocal: string): boolean {
  const key = `plan-seen:${period}:${startAtLocal}`;
  return localStorage.getItem(key) !== null;
}

export const localStorageService = {
  readMessages,
  upsertMessages,
  clearAllMessages,
  markPeriodSeen,
  isPeriodSeen,
};

export type LocalStorageService = typeof localStorageService;
